import React from "react";
import { FF, isFlagOn } from "./lib/featureFlags";
import { Authenticator, useAuthenticator, View, Text } from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { fetchAuthSession, signOut as amplifySignOut } from "aws-amplify/auth";
import { clearCorporateImportStore } from "./lib/corporateImport/queueStore";
import { AuthProvider, useAuth } from "./hooks/useAuth";
import { getMyTabPrefs, clearApiCache } from "./services/api";
import { PatientsProvider } from "./hooks/PatientsContext";
import { RBACProvider } from "./components/RBAC/RBACContext";
import SessionTimeoutModal from "./components/Security/SessionTimeoutModal";
import ProtectedRoute from "./components/auth/ProtectedRoute";
// MainLayout.jsx stays in the tree as an unused file until Stage E
// per the cutover plan; the import is removed now to keep this file lint-clean.

// ── Resilient lazy loader ──────────────────────────────────────────────
// Retries a dynamic import a few times with backoff before failing. Smooths
// over transient network blips; the ErrorBoundary below is the backstop that
// hard-reloads for genuinely stale chunk paths after a deploy. No auth code.
function lazyWithRetry(factory, retries = 2, backoffMs = 400) {
  return React.lazy(() =>
    (async () => {
      let lastErr;
      for (let attempt = 0; attempt <= retries; attempt++) {
        try {
          return await factory();
        } catch (e) {
          lastErr = e;
          if (attempt < retries) await new Promise((r) => setTimeout(r, backoffMs * (attempt + 1)));
        }
      }
      throw lastErr; // exhausted → ErrorBoundary handles (reloads for chunk errors)
    })()
  );
}

// ── Route guard ────────────────────────────────────────────────────
// ProtectedRoute extracted to ./components/auth/ProtectedRoute (R1.7) so it is
// reusable and unit-testable; behavior is unchanged.

// Redirect /patients-new/:id → /patients/:id (preserves the id param so old
// bookmarks keep working after the Stage-C patient-detail cutover).
function PatientsNewRedirect() {
  const { id } = useParams();
  return <Navigate to={`/patients/${id}`} replace />;
}

// ── Error Boundary (Upgraded to Urban Dark) ─────────────────────────────
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error("VitalCare ErrorBoundary caught:", error, info);

    const msg = (error?.message || "").toLowerCase();
    // Cover webpack + native ESM dynamic-import failure phrasings.
    const isChunkError = msg.includes("loading chunk") ||
                         msg.includes("chunkloaderror") ||
                         msg.includes("dynamically imported module") ||
                         msg.includes("importing a module script failed") ||
                         msg.includes("failed to fetch dynamically") ||
                         error?.name === "ChunkLoadError";

    if (isChunkError) {
      // Bounded, self-resetting reloads: up to 2 within a 30s window (guards
      // against an infinite loop) but resets after a quiet period so a later
      // deploy still gets fresh reloads.
      const KEY = "chunk_reload";
      let st = {};
      try { st = JSON.parse(sessionStorage.getItem(KEY) || "{}"); } catch (e) { st = {}; }
      const now = Date.now();
      if (!st.t || now - st.t > 30000) st = { n: 0 };
      if ((st.n || 0) < 2) {
        try { sessionStorage.setItem(KEY, JSON.stringify({ n: (st.n || 0) + 1, t: now })); } catch (e) {}
        console.warn(`Chunk load error — hard reload attempt ${(st.n || 0) + 1}/2.`);
        window.location.reload();
        return;
      }
      console.warn("Chunk load error persisted after reloads — showing error UI.");
      sessionStorage.removeItem(KEY);
    } else {
      sessionStorage.removeItem("chunk_reload");
    }
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center",
          background:"#f4f6fb", color:"#0f172a", fontFamily:"Inter,system-ui,sans-serif"
        }}>
          <div style={{
            background:"#ffffff", border:"1px solid #e2e8f0", borderRadius:16,
            padding:"40px 36px", maxWidth:440, textAlign:"center",
            boxShadow:"0 10px 30px rgba(15,23,42,0.12)"
          }}>
            <div style={{ fontSize:36, marginBottom:12 }}>⚠️</div>
            <h2 style={{ fontSize:18, fontWeight:700, color:"#0f172a", marginBottom:8 }}>
              Something went wrong
            </h2>
            <p style={{ fontSize:13, color:"#64748b", lineHeight:1.6, marginBottom:6 }}>
              This page encountered an error. Your data is safe.
            </p>
            <p style={{ fontSize:11, color:"#94a3b8", lineHeight:1.5, marginBottom:20,
              background:"#f4f6fb", borderRadius:8, padding:"8px 12px", textAlign:"left",
              wordBreak:"break-word", maxHeight:80, overflowY:"auto", border:"1px solid #e2e8f0" }}>
              {this.state.error?.message || "Unknown error"}
            </p>
            <div style={{ display:"flex", gap:10, justifyContent:"center" }}>
              <button onClick={() => window.location.reload()} style={{
                padding:"10px 24px", borderRadius:9, cursor:"pointer",
                border:"none", background:"linear-gradient(135deg,#2563eb,#1e3a8a)",
                color:"#fff", fontSize:13, fontWeight:700, fontFamily:"inherit"
              }}>
                Reload Page
              </button>
              <button onClick={() => { window.location.href = "/dashboard"; }} style={{
                padding:"10px 24px", borderRadius:9, cursor:"pointer",
                border:"1px solid #e2e8f0", background:"transparent",
                color:"#64748b", fontSize:13, fontWeight:600, fontFamily:"inherit"
              }}>
                Go to Dashboard
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ── Loading Spinner (Upgraded to Urban Dark) ──────────────────────────────
function PageLoader() {
  return (
    <div style={{
      display:"flex", alignItems:"center", justifyContent:"center",
      height:"60vh", color:"#64748b", fontSize:13, gap:10
    }}>
      <div style={{
        width:20, height:20, border:"2px solid #e2e8f0",
        borderTopColor:"#2563eb", borderRadius:"50%",
        animation:"spin 0.8s linear infinite"
      }} />
      Loading...
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ── All pages lazy-loaded ─────────────────────────────────────────────────
const DashboardPage       = lazyWithRetry(() => import("./pages/DashboardPage"));
const PatientsPage        = lazyWithRetry(() => import("./pages/PatientsPage"));
const NoGoPage            = lazyWithRetry(() => import("./pages/NoGoPage"));
const AnalyticsPage       = lazyWithRetry(() => import("./pages/AnalyticsPage"));
// ✅ REMOVED legacy SOCPage and MarginPage! PatientsPage handles them now!
const PriorAuthPage       = lazyWithRetry(() => import("./pages/PriorAuthPage"));
const PharmReviewPage     = lazyWithRetry(() => import("./pages/PharmReviewPage"));
const CommandCenterPage   = lazyWithRetry(() => import("./pages/CommandCenterPage"));
const CommissionStructurePage = lazyWithRetry(() => import("./pages/CommissionStructurePage"));
const PAIntelligenceHubPage = lazyWithRetry(() => import("./pages/PAIntelligenceHubPage"));
const SettingsPage        = lazyWithRetry(() => import("./pages/SettingsPage"));
const AEDashboardPage      = lazyWithRetry(() => import("./pages/AEDashboardPage"));
const PharmacyWorkflowPage = lazyWithRetry(() => import("./pages/PharmacyWorkflowPage"));
const InventoryPage = lazyWithRetry(() => import("./pages/InventoryPage"));
const FinancialClaimsPage  = lazyWithRetry(() => import("./pages/FinancialClaimsPage"));
const FinancialHubPage    = lazyWithRetry(() => import("./pages/FinancialHubPage"));
const FaxInboxPage        = lazyWithRetry(() => import("./pages/FaxInboxPage"));
const FaxInboxNewPage     = lazyWithRetry(() => import("./pages/FaxInboxNewPage"));
const CommunicationsPage  = lazyWithRetry(() => import("./pages/CommunicationsPage")); // Step 3.10
const DrugCatalogPage     = lazyWithRetry(() => import("./pages/DrugCatalogPage"));
const DrugPriceUploadPage = lazyWithRetry(() => import("./pages/DrugPriceUploadPage"));
const BlankEntryFixerPage = lazyWithRetry(() => import("./pages/BlankEntryFixerPage"));
const TherapyTypePage     = lazyWithRetry(() => import("./pages/TherapyTypePage"));
const AIReferralsPage     = lazyWithRetry(() => import("./pages/AIReferralsPage"));
const FormularyPage       = lazyWithRetry(() => import("./pages/FormularyPage"));
const UserManagementPage  = lazyWithRetry(() => import("./pages/UserManagementPage"));
const PAChecklistPage     = lazyWithRetry(() => import("./pages/PAChecklistPage"));
const DischargeRxPage     = lazyWithRetry(() => import("./pages/DischargeRxPage"));
const OwnerFinancialIntelPage = lazyWithRetry(() => import("./pages/OwnerFinancialIntelPage"));
const RoleDashboardRouter = lazyWithRetry(() => import("./components/Dashboard/RoleDashboardRouter"));
const DashboardV2         = lazyWithRetry(() => import("./pages/DashboardV2"));
const UniversalPatientView= lazyWithRetry(() => import("./components/Patient/UniversalPatientView"));
const PatientDetailNewPage= lazyWithRetry(() => import("./pages/PatientDetailNewPage"));
const TeamManagementPage  = lazyWithRetry(() => import("./components/RBAC/TeamManagement"));
const AdminPricingPage    = lazyWithRetry(() => import("./pages/AdminPricingPage"));
const PolicyScraperPage   = lazyWithRetry(() => import("./pages/PolicyScraperPage"));
const ReferralQueuePage   = lazyWithRetry(() => import("./pages/ReferralQueuePage"));
const ReferralsListPage   = lazyWithRetry(() => import("./pages/ReferralsListPage"));
const DashboardNewPage    = lazyWithRetry(() => import("./pages/DashboardNewPage"));
const ProfitPrioritiesPage= lazyWithRetry(() => import("./pages/ProfitPrioritiesPage"));
const CostAnalysisPage    = lazyWithRetry(() => import("./pages/CostAnalysisPage"));
const PurchasePlanPage    = lazyWithRetry(() => import("./pages/PurchasePlanPage"));
const SchedulerPage       = lazyWithRetry(() => import("./pages/SchedulerPage"));
const ReferralFlowPage    = lazyWithRetry(() => import("./pages/ReferralFlowPage"));
const ReferralFlowClinicSnapshot = lazyWithRetry(() => import("./pages/ReferralFlow/ClinicSnapshot")); // Item #IR · V3 — printable clinic snapshot
const BooksBridgePage     = lazyWithRetry(() => import("./pages/BooksBridgePage")); // Item #IR · V6 — VitalBooks manual-upload bridge (flag REACT_APP_FF_BOOKS_BRIDGE)
const PrescriberPortalPage = lazyWithRetry(() => import("./pages/PrescriberPortalPage")); // Item #IR Phase 5 · public F22 page
const RxAuthTrackerPage   = lazyWithRetry(() => import("./pages/RxAuthTrackerPage"));
const RemittancePage      = lazyWithRetry(() => import("./pages/RemittancePage"));
const DenialIntelligencePage  = lazyWithRetry(() => import("./pages/DenialIntelligencePage"));
const BillingIntelligencePage = lazyWithRetry(() => import("./pages/BillingIntelligencePage"));
const CommissionPage      = lazyWithRetry(() => import("./pages/CommissionPage"));
const ClinicROIPage       = lazyWithRetry(() => import("./pages/ClinicROIPage"));
const DispensingPage      = lazyWithRetry(() => import("./pages/DispensingPage"));
const RefillIntelligencePage = lazyWithRetry(() => import("./pages/RefillIntelligencePage"));
const MarginOptimizerPage = lazyWithRetry(() => import("./pages/MarginOptimizerPage"));
const IntakeDashboardPage = lazyWithRetry(() => import("./pages/IntakeDashboardPage"));
const IntakePage          = lazyWithRetry(() => import("./pages/IntakePage"));
const PAAnalysisPage      = lazyWithRetry(() => import("./pages/PAAnalysisPage"));
const FinancialDeepDivePage = lazyWithRetry(() => import("./pages/FinancialDeepDivePage"));
const BillPayPage         = lazyWithRetry(() => import("./pages/BillPayPage"));
const CashFlowPage        = lazyWithRetry(() => import("./pages/CashFlowPage"));
const FinStatementsPage   = lazyWithRetry(() => import("./pages/FinStatementsPage"));
const AuditLogPage        = lazyWithRetry(() => import("./pages/AuditLogPage"));
const HipaaCompliancePage = lazyWithRetry(() => import("./pages/HipaaCompliancePage"));
const CalendarSyncPage    = lazyWithRetry(() => import("./pages/CalendarSyncPage"));
const RedesignLayout      = lazyWithRetry(() => import("./components/redesign/RedesignLayout"));
const SocDashboardPage    = lazyWithRetry(() => import("./pages/SocDashboardPage"));
const CommandCenterV2Page = lazyWithRetry(() => import("./pages/CommandCenterV2Page"));
const PerformanceV2Page   = lazyWithRetry(() => import("./pages/PerformanceV2Page"));

// ── HIPAA: clear Cognito tokens when ALL tabs are closed ──────────────────
(function() {
  try {
    const TAB_KEY = "vc_open_tabs";
    const tabCount = parseInt(localStorage.getItem(TAB_KEY) || "0") + 1;
    localStorage.setItem(TAB_KEY, String(tabCount));

    window.addEventListener("beforeunload", () => {
      const count = parseInt(localStorage.getItem(TAB_KEY) || "1") - 1;
      if (count <= 0) {
        localStorage.setItem(TAB_KEY, "0");
        Object.keys(localStorage).forEach(k => {
          if (k.includes("CognitoIdentityServiceProvider") ||
              k.includes("amplify") ||
              k.includes("vc_auth")) {
            localStorage.removeItem(k);
          }
        });
      } else {
        localStorage.setItem(TAB_KEY, String(count));
      }
    });
  } catch(e) {
    console.warn("Tab tracker init failed:", e.message);
  }
})();

// ── HIPAA: auto-logout after 30 min inactivity ───────────────────────────
const IDLE_TIMEOUT_MS  = 30 * 60 * 1000;
const WARN_BEFORE_MS   =  1 * 60 * 1000;

function useIdleLogout(signOutFn) {
  const timerRef = React.useRef(null);
  const warnRef  = React.useRef(null);
  const [warning, setWarning] = React.useState(false);

  React.useEffect(() => {
    function reset() {
      setWarning(false);
      clearTimeout(timerRef.current);
      clearTimeout(warnRef.current);
      warnRef.current = setTimeout(() => setWarning(true), IDLE_TIMEOUT_MS - WARN_BEFORE_MS);
      timerRef.current = setTimeout(async () => {
        setWarning(false);
        clearCorporateImportStore(); clearApiCache(); // Bulk Review Queue sheet + decisions (sessionStorage) and cached API lists go with the session
        try { await amplifySignOut(); } catch(e) {}
        window.location.reload();
      }, IDLE_TIMEOUT_MS);
    }

    const events = ["mousemove","mousedown","keydown","touchstart","scroll","click"];
    events.forEach(e => window.addEventListener(e, reset, { passive: true }));
    reset();

    return () => {
      events.forEach(e => window.removeEventListener(e, reset));
      clearTimeout(timerRef.current);
      clearTimeout(warnRef.current);
    };
  }, [signOutFn]);

  return warning;
}

// ── Login Screen (Upgraded to Urban Dark) ─────────────────────────────────
const LoginComponents = {
  Header() {
    return (
      <View textAlign="center" padding="2rem 2rem 0.5rem">
        <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:10,marginBottom:6}}>
          <div style={{width:42,height:42,borderRadius:11,background:"linear-gradient(135deg,#2563eb,#1e3a8a)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,boxShadow:"0 4px 12px rgba(37,99,235,0.3)"}}>
            💉
          </div>
          <div style={{textAlign:"left"}}>
            <div style={{fontWeight:700,fontSize:18,color:"#0f172a",lineHeight:1.2}}>VitalCare</div>
            <div style={{fontSize:11,color:"#64748b"}}>SOC Tracker</div>
          </div>
        </div>
        <div style={{fontSize:12,color:"#94a3b8",paddingBottom:4}}>HIPAA-Compliant · AWS Secured</div>
      </View>
    );
  },
  Footer() {
    return (
      <View textAlign="center" padding="0 0 1.5rem">
        <Text style={{fontSize:11,color:"#94a3b8"}}>
          © 2026 VitalCare of Tacoma · Protected Health Information
        </Text>
      </View>
    );
  },
};

// ── App Routes ────────────────────────────────────────────────────────────
function AppRoutes() {
  const { user, signOut: amplifyUiSignOut } = useAuthenticator((ctx) => [ctx.user]);
  const signOut = React.useCallback((...a) => { clearCorporateImportStore(); clearApiCache(); return amplifyUiSignOut(...a); }, [amplifyUiSignOut]);
  const [userProfile, setUserProfile] = React.useState(null);
  const idleWarning = useIdleLogout(signOut);

  React.useEffect(() => {
    async function loadRole() {
      try {
        // Force refresh on first load to avoid stale token errors
        const session = await fetchAuthSession({ forceRefresh: false });
        const payload = session?.tokens?.idToken?.payload;
        if (!payload) {
          // Token missing — try force refresh
          console.warn("No token payload, force refreshing...");
          const refreshed = await fetchAuthSession({ forceRefresh: true });
          const rp = refreshed?.tokens?.idToken?.payload;
          if (rp) {
            const r = (rp["custom:Role"] || rp["custom:role"] || "ae").toLowerCase();
            setUserProfile({ user, role: r, name: rp.name || rp.email || "", email: rp.email || "", signOut });
            return;
          }
        }
        const r = (payload?.["custom:Role"] || payload?.["custom:role"] || "ae").toLowerCase();
        const email = payload?.email || "";
        const name = payload?.name || email;
        setUserProfile({ user, role: r, name, email, signOut });
      } catch (e) {
        console.error("Role load error:", e);
        // Try force refresh on error
        try {
          const refreshed = await fetchAuthSession({ forceRefresh: true });
          const rp = refreshed?.tokens?.idToken?.payload;
          if (rp) {
            setUserProfile({ user, role: (rp["custom:Role"] || rp["custom:role"] || "ae").toLowerCase(), name: rp.name || rp.email || "", email: rp.email || "", signOut });
            return;
          }
        } catch (e2) {
          console.error("Force refresh also failed:", e2);
        }
        setUserProfile({ user, role: "ae", name: "", email: "", signOut });
      }
    }
    if (user) loadRole();
  }, [user, signOut]);

  // Per-user sidebar tab access (owner-selected, stored server-side). Fetched
  // once the profile is known and merged in. Until it resolves — or if the
  // fetch fails / no restriction is set — allowedTabs stays null, meaning full
  // role access (fail-open, never lock a user out on a prefs hiccup).
  React.useEffect(() => {
    if (!userProfile?.email) return;
    let alive = true;
    getMyTabPrefs()
      .then((r) => { if (alive) setUserProfile((p) => (p ? { ...p, allowedTabs: r?.allowedTabs ?? null } : p)); })
      .catch(() => { if (alive) setUserProfile((p) => (p ? { ...p, allowedTabs: null } : p)); });
    return () => { alive = false; };
  }, [userProfile?.email]);

  if (!userProfile) return <div style={{padding:40,textAlign:"center",color:"#64748b"}}>Loading...</div>;

  // Nurse is a scoped role (Referrals view, Scheduler, Patient Notes) — access is
  // gated by RBAC (PERMISSIONS + ROUTE_ALLOW + nurse NAV_BY_ROLE), not blocked here.

  const defaultPath = "/dashboard";

  return (
    <AuthProvider value={userProfile}>
      <RBACProvider>
        <PatientsProvider>
          <SessionTimeoutModal />
          {/* ── HIPAA idle warning banner ── */}
          {idleWarning && (
            <div style={{
              position:"fixed",top:0,left:0,right:0,zIndex:99999,
              background:"#ea580c",color:"#fff",
              padding:"12px 20px",textAlign:"center",
              fontSize:14,fontWeight:700,
              boxShadow:"0 4px 20px rgba(234,88,12,0.4)",
            }}>
              ⚠️ You will be automatically logged out in 1 minute due to inactivity.
              Move your mouse or press any key to stay logged in.
            </div>
          )}
          <BrowserRouter>
            <ErrorBoundary>
              <React.Suspense fallback={<PageLoader />}>
                <Routes>
                  {/* Stage A of the redesign cutover: RedesignLayout (new
                       Sidebar + TopBar) is now the shell for every route.
                       MainLayout.jsx stays in the tree as an unused file
                       and will be deleted in Stage E. Legacy screens
                       render their existing content inside the new chrome
                       until each is replaced screen-by-screen in Stage C. */}
                  <Route element={<RedesignLayout />}>
                    <Route path="/" element={<Navigate to={defaultPath} replace />} />
                    
                    {/* /dashboard is the SOC Dashboard v2 (own shell, mounted
                         below, outside RedesignLayout). The previous Stage B
                         screen stays reachable at /dashboard-classic, DashboardV2
                         at /dashboard-v2; /dashboard-new redirects to /dashboard
                         so existing bookmarks keep working. */}
                    <Route path="/dashboard-classic" element={<ProtectedRoute allow={["owner","pharmacist","technician","intake","billing","ae","liaison","scheduler","nurse"]}><DashboardNewPage /></ProtectedRoute>} />
                    <Route path="/dashboard-v2"     element={<DashboardV2 />} />
                    <Route path="/dashboard-legacy" element={<RoleDashboardRouter />} />
                    <Route path="/dashboard-old"    element={<DashboardPage />} />
                    {/* 👇 THIS IS THE FIX: Route margin and soc to the new PatientsPage! */}
                    <Route path="/patients" element={<PatientsPage />} />
                    <Route path="/soc" element={<PatientsPage />} />
                    <Route path="/margin" element={<PatientsPage />} />
                    
                    {/* Stage-C patient-detail cutover: /patients/:id is now the
                         redesigned screen. The legacy UniversalPatientView is
                         preserved at /patients-v2/:id as a rollback fallback; the
                         /patients-new/:id path redirects to /patients/:id so any
                         existing bookmarks keep working. */}
                    <Route path="/patients/:id" element={<PatientDetailNewPage />} />
                    <Route path="/patients-v2/:id" element={<UniversalPatientView />} />
                    <Route path="/patients-new/:id" element={<PatientsNewRedirect />} />
                    <Route path="/no-go" element={<NoGoPage />} />
                    <Route path="/nogo" element={<Navigate to="/no-go" replace />} />
                    <Route path="/analytics" element={<AnalyticsPage />} />
                    {/* Profit-first prioritization — read-only; reuses the Analytics
                         P&L margin engine. Owner/pharmacist only (margin-data gating). */}
                    <Route path="/profit-priorities" element={<ProtectedRoute allow={["owner","pharmacist"]}><ProfitPrioritiesPage /></ProtectedRoute>} />
                    <Route path="/pa-review" element={<PriorAuthPage />} />
                    <Route path="/settings" element={<SettingsPage />} />
                    <Route path="/settings-crm" element={<Navigate to="/settings" replace />} />
                    <Route path="/ae-dashboard" element={<ProtectedRoute allow={["owner","ae","pharmacist","technician","intake","billing","scheduler","liaison"]}><AEDashboardPage /></ProtectedRoute>} />
                    <Route path="/ae-actions" element={<ProtectedRoute allow={["owner","ae","pharmacist","technician","intake","billing","scheduler","liaison"]}><AEDashboardPage /></ProtectedRoute>} />
                    <Route path="/pharm-review" element={<ProtectedRoute allow={["owner","pharmacist","technician"]}><PharmReviewPage /></ProtectedRoute>} />
                    <Route path="/command-center-v2" element={<Navigate to="/command-center" replace />} />
                    <Route path="/command-center-legacy" element={<ProtectedRoute allow={["owner"]}><CommandCenterPage /></ProtectedRoute>} />
                    <Route path="/commission-structure" element={<ProtectedRoute allow={["owner","billing","ae"]}><CommissionStructurePage /></ProtectedRoute>} />
                    <Route path="/pa-intelligence" element={<ProtectedRoute allow={["owner","pharmacist","technician","intake"]}><PAIntelligenceHubPage /></ProtectedRoute>} />
                    <Route path="/financial-claims" element={<ProtectedRoute allow={["owner","billing"]}><FinancialClaimsPage /></ProtectedRoute>} />
                    {/* Fax Inbox cutover (2026-08-20, owner-approved visual diff): the
                         Lovable-design FaxInboxPage is now the primary /fax-inbox.
                         FaxInboxNewPage stays mounted inside /intake (?view=fax) and
                         remains reachable at /fax-inbox-legacy;
                         /fax-inbox-new redirects to /fax-inbox for old links. */}
                    <Route path="/fax-inbox" element={<ProtectedRoute allow={["owner","pharmacist","technician","intake"]}><FaxInboxPage /></ProtectedRoute>} />
                    <Route path="/fax-inbox-legacy" element={<ProtectedRoute allow={["owner","pharmacist","technician","intake"]}><FaxInboxNewPage /></ProtectedRoute>} />
                    {/* Step 3.10 — Communications: the outbound-task approval queue (3.9 Missing-Info loop) */}
                    <Route path="/communications" element={<ProtectedRoute allow={["owner","pharmacist","technician","intake"]}><CommunicationsPage /></ProtectedRoute>} />
                    <Route path="/fax-inbox-new" element={<Navigate to="/fax-inbox" replace />} />
                    {/* Figma nav — new screens + honest placeholders */}
                    {/* Owner correction #2 (2026-08-12): aggregate cost-analysis view — owner/billing only (pharmacist removed) */}
                    <Route path="/cost-analysis" element={<ProtectedRoute allow={["owner","billing"]}><CostAnalysisPage /></ProtectedRoute>} />
                    <Route path="/purchase-plan" element={<ProtectedRoute allow={["owner","pharmacist","billing","technician","intake","scheduler","ae","liaison","nurse"]}><PurchasePlanPage /></ProtectedRoute>} />
                    {/* Scheduler (Phase 1) — parallel route; calendar reads real events
                         (empty until the reviewed Lambda deploy) + live Ready-to-Schedule
                         queue. Viewers + managers; legacy /soc untouched. */}
                    <Route path="/scheduler-new" element={<ProtectedRoute allow={["owner","pharmacist","scheduler","technician","nurse"]}><SchedulerPage /></ProtectedRoute>} />
                    <Route path="/pharmacy-workflow" element={<ProtectedRoute allow={["owner","pharmacist","technician"]}><PharmacyWorkflowPage /></ProtectedRoute>} />
                    <Route path="/inventory" element={<ProtectedRoute allow={["owner","pharmacist","technician"]}><InventoryPage /></ProtectedRoute>} />
                    <Route path="/audit-log" element={<ProtectedRoute allow={["owner"]}><AuditLogPage /></ProtectedRoute>} />
                    <Route path="/hipaa-compliance" element={<ProtectedRoute allow={["owner"]}><HipaaCompliancePage /></ProtectedRoute>} />
                    <Route path="/pa-analysis" element={<ProtectedRoute allow={["owner","pharmacist","technician","intake"]}><PAAnalysisPage /></ProtectedRoute>} />
                    <Route path="/policy-scraper" element={<PolicyScraperPage />} />
                    {/* Full Figma design — every tab below is wired to live data (no preview states). */}
                    <Route path="/rx-auth-tracker" element={<ProtectedRoute allow={["owner","pharmacist","technician","intake"]}><RxAuthTrackerPage /></ProtectedRoute>} />
                    <Route path="/remittance" element={<ProtectedRoute allow={["owner","billing"]}><RemittancePage /></ProtectedRoute>} />
                    <Route path="/denial-intel" element={<ProtectedRoute allow={["owner","billing"]}><DenialIntelligencePage /></ProtectedRoute>} />
                    <Route path="/billing-intel" element={<ProtectedRoute allow={["owner","billing"]}><BillingIntelligencePage /></ProtectedRoute>} />
                    <Route path="/commission" element={<ProtectedRoute allow={["owner","ae"]}><CommissionPage /></ProtectedRoute>} />
                    <Route path="/clinic-roi" element={<ProtectedRoute allow={["owner","billing"]}><ClinicROIPage /></ProtectedRoute>} />
                    <Route path="/dispensing" element={<ProtectedRoute allow={["owner","pharmacist","technician"]}><DispensingPage /></ProtectedRoute>} />
                    <Route path="/refill-intel" element={<ProtectedRoute allow={["owner","pharmacist","billing"]}><RefillIntelligencePage /></ProtectedRoute>} />
                    <Route path="/margin-optimizer" element={<ProtectedRoute allow={["owner","pharmacist","billing"]}><MarginOptimizerPage /></ProtectedRoute>} />
                    {/* D-108/D-109 split-intake architecture: /intake is the tabbed
                         "Intake & Referrals" workspace (NewPatientIntakeForm +
                         RefillIntakeForm). The old gatekeeper PAGE is removed; the
                         server-side /gatekeeper/evaluate engine and its RBAC are
                         untouched. Same role set the gatekeeper route used. */}
                    {/* Item #IR · Referral Flow — six-step flow; master flag off → legacy /intake (route still resolves, no new surface). */}
                    <Route path="/referral-flow" element={isFlagOn(FF.REFERRAL_FLOW) ? <ProtectedRoute allow={["owner","pharmacist","technician","intake","billing","ae","liaison","scheduler"]}><ReferralFlowPage /></ProtectedRoute> : <Navigate to="/intake?view=fax" replace />} />
                    {/* Item #IR · V3 — printable clinic snapshot (AE shows a referral source its progress; no $ / commission; initials only). Same roles + flag as /referral-flow; the API scopes AEs to their own accounts. */}
                    <Route path="/referral-flow/snapshot/:clinicKey" element={isFlagOn(FF.REFERRAL_FLOW) ? <ProtectedRoute allow={["owner","pharmacist","technician","intake","billing","ae","liaison","scheduler"]}><ReferralFlowClinicSnapshot /></ProtectedRoute> : <Navigate to="/intake?view=fax" replace />} />
                    <Route path="/intake" element={<ProtectedRoute allow={["owner","pharmacist","technician","intake","billing"]}><IntakePage /></ProtectedRoute>} />
                    <Route path="/intake-dashboard" element={<ProtectedRoute allow={["owner","pharmacist","technician","intake","billing"]}><IntakeDashboardPage /></ProtectedRoute>} />
                    <Route path="/deep-dive" element={<ProtectedRoute allow={["owner","billing"]}><FinancialDeepDivePage /></ProtectedRoute>} />
                    <Route path="/performance" element={<ProtectedRoute allow={["owner","pharmacist","technician","intake","billing","ae","liaison","scheduler"]}><PerformanceV2Page /></ProtectedRoute>} />
                    <Route path="/performance-v2" element={<Navigate to="/performance" replace />} />
                    <Route path="/bill-pay" element={<ProtectedRoute allow={["owner","billing"]}><BillPayPage /></ProtectedRoute>} />
                    <Route path="/cash-flow" element={<ProtectedRoute allow={["owner"]}><CashFlowPage /></ProtectedRoute>} />
                    {/* Item #IR · V6 — VitalBooks Bridge (manual export → upload → reconcile; owner + billing). Flag off → the Cash Flow Plan it feeds. */}
                    <Route path="/books-bridge" element={isFlagOn(FF.BOOKS_BRIDGE) ? <ProtectedRoute allow={["owner","billing"]}><BooksBridgePage /></ProtectedRoute> : <Navigate to="/cash-flow" replace />} />
                    <Route path="/fin-statements" element={<ProtectedRoute allow={["owner","billing"]}><FinStatementsPage /></ProtectedRoute>} />
                    <Route path="/financial-hub" element={<ProtectedRoute allow={["owner"]}><FinancialHubPage /></ProtectedRoute>} />
                    {/** Owner Dashboard /cfo-command removed — superseded by Owner Strip (DB16). */}
                    <Route path="/calendar-sync" element={<ProtectedRoute allow={["owner","scheduler","pharmacist","technician"]}><CalendarSyncPage /></ProtectedRoute>} />
                    <Route path="/drug-catalog" element={<DrugCatalogPage />} />
                    <Route path="/pricing-upload" element={<DrugPriceUploadPage />} />
                    <Route path="/entry-fixer" element={<BlankEntryFixerPage />} />
                    <Route path="/therapy-types" element={<TherapyTypePage />} />
                    <Route path="/ai-referrals" element={<AIReferralsPage />} />
                    <Route path="/referral-queue" element={<ReferralQueuePage />} />
                    <Route path="/formulary" element={<FormularyPage />} />
                    {/* R1.7 (R-29): route-level owner gate (was component-gated only). Server /admin/users* is already owner-only; this stops the page shell rendering for non-owners on direct URL. */}
                    <Route path="/user-management" element={<ProtectedRoute allow={["owner"]}><UserManagementPage /></ProtectedRoute>} />
                    <Route path="/team-management" element={<TeamManagementPage />} />
                    <Route path="/pa-checklist" element={<PAChecklistPage />} />
                    <Route path="/discharge-rx" element={<DischargeRxPage />} />
                    {/* R1.7 (R-29): owner-only financial workspace — add the missing route-level gate to match the owner-only nav + content. */}
                    <Route path="/financial-intel" element={<ProtectedRoute allow={["owner"]}><OwnerFinancialIntelPage /></ProtectedRoute>} />
                    {/* R1.7 (R-29): pricing administration was owner-gated only inside the component; add the route guard too. */}
                    <Route path="/admin-pricing" element={<ProtectedRoute allow={["owner"]}><AdminPricingPage /></ProtectedRoute>} />

                    {/* Stage B route swap: /referrals is now the primary
                         redesigned referrals path. The -new paths redirect
                         to the primary paths so existing bookmarks keep
                         working. */}
                    <Route path="/referrals"     element={<ProtectedRoute allow={["owner","pharmacist","technician","intake","billing","ae","liaison","scheduler","nurse"]}><ReferralsListPage /></ProtectedRoute>} />
                    <Route path="/referrals-new" element={<Navigate to="/referrals" replace />} />
                    <Route path="/dashboard-new" element={<Navigate to="/dashboard" replace />} />

                    {/* Fallback for unknown URLs */}
                    <Route path="*" element={<Navigate to={defaultPath} replace />} />
                  </Route>
                  {/* SOC Dashboard v2 ships its own sidebar + header, so it
                       renders outside RedesignLayout. Same role gate as the
                       classic dashboard. */}
                  <Route path="/dashboard" element={<ProtectedRoute allow={["owner","pharmacist","technician","intake","billing","ae","liaison","scheduler","nurse"]}><SocDashboardPage /></ProtectedRoute>} />
                  {/* Command Center v2 ships the same live sidebar + header as
                       /dashboard, so it also renders outside RedesignLayout
                       (no duplicate chrome). Same role gate as before. */}
                  <Route path="/command-center" element={<ProtectedRoute allow={["owner","pharmacist","technician","intake","billing","ae","liaison","scheduler"]}><CommandCenterV2Page /></ProtectedRoute>} />
                </Routes>
              </React.Suspense>
            </ErrorBoundary>
          </BrowserRouter>
        </PatientsProvider>
      </RBACProvider>
    </AuthProvider>
  );
}

// ── Root App ──────────────────────────────────────────────────────────────
export default function App() {
  // Item #IR Phase 5 (F22) — the ONLY public (no-login) page: the redacted prescriber
  // status link. Mounted before the Authenticator so a prescriber office never sees a
  // login wall; the page itself renders "This link is not active" unless
  // REACT_APP_FF_PRESCRIBER_PORTAL=true and the backend token check passes.
  if (typeof window !== "undefined" && /^\/prescriber-portal\/[^/]+/.test(window.location.pathname)) {
    return (
      <ErrorBoundary>
        <BrowserRouter><React.Suspense fallback={<PageLoader />}><Routes><Route path="/prescriber-portal/:token" element={<PrescriberPortalPage />} /></Routes></React.Suspense></BrowserRouter>
      </ErrorBoundary>
    );
  }
  return (
    <ErrorBoundary>
      <Authenticator components={LoginComponents} hideSignUp={true}>
        {() => <AppRoutes />}
      </Authenticator>
    </ErrorBoundary>
  );
}
